/** Zustände nach „Fertig“: Wartet auf Zahlung, Bezahlt, Mehrdeutig, Nicht gefunden. */
import { useEffect, useState } from "react";
import { Button, Icon, Notice, StatusBadge, Total } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { Textarea } from "../ui/Layout";
import { useAbbrechen, useNeuerWarenkorb, useSpaeter, useZuordnen, useZurueck, type HubStatus, type Kandidat, type WarenkorbAnsicht as WK } from "./api";
import { dauerSeit, formatEuro, formatUhrzeit } from "./format";
import { ApiFehler } from "../api/client";

function useTick(ms = 1000) {
  const [, setN] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

function Positionen({ warenkorb, titel }: { warenkorb: WK; titel?: string }) {
  return (
    <>
      <div className="sc-spread">
        <h2 className="heading">{titel ?? "Warenkorb"}</h2>
        <span className="sc-muted">{warenkorb.positionen.length} Artikel</span>
      </div>
      <div>
        {warenkorb.positionen.map((p) => (
          <ItemRow key={p.id} title={p.bezeichnung} photo={p.art === "ladenware" ? false : undefined} meta={[p.regalNummer && `Regal ${p.regalNummer}`, p.mieterName ?? (p.art === "ladenware" ? "Ladenware" : null)].filter(Boolean).join(" · ")} price={p.endpreisCent} />
        ))}
      </div>
    </>
  );
}

export function Wartet({ warenkorb, sumupVerbunden, hub }: { warenkorb: WK; sumupVerbunden: boolean; hub?: HubStatus }) {
  useTick();
  const zurueck = useZurueck();
  const abbrechen = useAbbrechen();
  const dauer = dauerSeit(warenkorb.abgeschlossenAt);
  const lang = warenkorb.abgeschlossenAt ? Date.now() - new Date(warenkorb.abgeschlossenAt).getTime() > 3 * 60_000 : false;
  return (
    <div className="sc-kasse">
      <section className="sc-kasse__cart kasse-center" aria-live="polite">
        <Total amountCent={warenkorb.summeCent} label="Zu zahlen" />
        <p className="body-lg">Diesen Betrag in SumUp kassieren</p>
        <StatusBadge status="wait" size="lg">
          Suche Zahlung … seit <span className="kasse-timer">{dauer}</span>
        </StatusBadge>
        {!sumupVerbunden ? (
          <Notice tone="attention" title="SumUp ist nicht verbunden">Die Zahlung kann nicht automatisch erkannt werden. In der Verwaltung unter Einstellungen › Kasse & SumUp verbinden – oder den Warenkorb später zuordnen.</Notice>
        ) : hub?.sumup === "getrennt" ? (
          <Notice tone="error" title="Verbindung zu SumUp verloren">Die Anmeldung bei SumUp ist abgelaufen. Bitte in den Einstellungen neu verbinden. Zahlungen werden nachträglich zugeordnet.</Notice>
        ) : lang ? (
          <Notice tone="info" title="Das dauert länger als sonst">Wurde in SumUp schon kassiert? Bar oder Karte ist egal. Nach 15 Minuten fragt die Kasse, wie es weitergeht.</Notice>
        ) : (
          <p className="sc-muted">
            <Icon name="info" size={16} /> Bar oder Karte ist egal. Sobald SumUp die Zahlung meldet, geht es von selbst weiter.
          </p>
        )}
        <div className="sc-row">
          <Button variant="secondary" icon="chevron-left" onClick={() => zurueck.mutate({ id: warenkorb.id })} loading={zurueck.isPending}>
            Zurück zum Warenkorb
          </Button>
          <Button variant="quiet" onClick={() => confirm("Abbrechen? Die Artikel gehen zurück ins Regal.") && abbrechen.mutate({ id: warenkorb.id, grund: "Abbruch beim Warten auf Zahlung" })} loading={abbrechen.isPending}>
            Abbrechen
          </Button>
        </div>
      </section>
      <aside className="sc-kasse__side">
        <Positionen warenkorb={warenkorb} />
        <span className="sc-grow" />
        <p className="kasse-side__hint">
          Warenkorb geöffnet um {warenkorb.abgeschlossenAt ? formatUhrzeit(warenkorb.abgeschlossenAt) : "–"}. Artikel sind bis zur Zahlung reserviert.
        </p>
      </aside>
    </div>
  );
}

export function Bezahlt({ warenkorb }: { warenkorb: WK }) {
  const neu = useNeuerWarenkorb();
  const [sekunden, setSekunden] = useState(5);
  const [details, setDetails] = useState(false);
  const abgebrochen = warenkorb.status === "abgebrochen";
  useEffect(() => {
    if (details) return;
    const t = setInterval(() => setSekunden((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [details]);
  useEffect(() => {
    if (sekunden <= 0 && !neu.isPending) neu.mutate({});
  }, [sekunden]);
  const mieter = new Set(warenkorb.positionen.map((p) => p.mieterName).filter(Boolean)).size;
  return (
    <div className="kasse-center">
      {abgebrochen ? (
        <>
          <StatusBadge status="neutral" size="lg">
            {warenkorb.uebung ? "Übung beendet" : "Warenkorb abgebrochen"}
          </StatusBadge>
          <p className="sc-muted">{warenkorb.uebung ? "Nichts wurde verbucht – im echten Betrieb würde die Kasse jetzt auf die SumUp-Zahlung warten." : "Die Artikel gelten wieder als im Regal."}</p>
        </>
      ) : (
        <>
          <span className="sc-success-ring" aria-hidden="true">
            <Icon name="check" size={64} strokeWidth={3} />
          </span>
          <h1 className="display">Bezahlt</h1>
          <Total amountCent={warenkorb.summeCent} tone="success" label="Erhalten" />
          <p className="body-lg">
            <Icon name={warenkorb.zahlungsart === "bar" ? "banknote" : "wallet"} size={20} /> {warenkorb.zahlungsart === "bar" ? "Bar" : warenkorb.zahlungsart === "karte" ? "Karte" : "Zahlung"}
            {warenkorb.bezahltAt ? ` · ${formatUhrzeit(warenkorb.bezahltAt)}` : ""}
          </p>
          <p className="sc-muted">
            {warenkorb.positionen.length} Artikel verkauft{mieter ? ` · ${mieter} ${mieter === 1 ? "Mieter" : "Mieter"} benachrichtigt` : ""}
          </p>
        </>
      )}
      {details ? (
        <div className="kasse-optionen">
          <Positionen warenkorb={warenkorb} titel="Verkauf" />
        </div>
      ) : null}
      <div className="sc-col" style={{ alignItems: "center" }}>
        {!details ? <span className="sc-muted sc-small">Neuer Warenkorb in {Math.max(0, sekunden)} s</span> : null}
        <Button variant="primary" size="lg" iconEnd="chevron-right" onClick={() => neu.mutate({})} loading={neu.isPending}>
          Nächster Kunde
        </Button>
        {!abgebrochen ? (
          <Button variant="quiet" icon="receipt" onClick={() => setDetails((d) => !d)}>
            {details ? "Verkauf ausblenden" : "Verkauf anzeigen"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function KandidatOption({ k, gewaehlt, onWahl, mitAbweichung }: { k: Kandidat; gewaehlt: boolean; onWahl: () => void; mitAbweichung?: boolean }) {
  const vorMin = Math.round((Date.now() - new Date(k.zeitpunkt).getTime()) / 60_000);
  return (
    <button type="button" className="sc-option" aria-pressed={gewaehlt} onClick={onWahl}>
      <span className="sc-radio" aria-hidden="true" />
      <span className="sc-option__text">
        <span className="sc-strong">
          {formatEuro(k.betragCent)}
          {mitAbweichung && k.abweichungCent !== 0 ? <span className="sc-muted"> ({k.abweichungCent > 0 ? "+" : "−"}{formatEuro(Math.abs(k.abweichungCent))})</span> : null}
        </span>
        <span className="sc-muted sc-small">
          <Icon name={k.zahlungsart === "bar" ? "banknote" : "wallet"} size={16} /> {k.zahlungsart === "bar" ? "Bar" : "Karte"} · {formatUhrzeit(k.zeitpunkt, true)}
          {k.kartendetails ? ` · ${k.kartendetails}` : ""} · vor {vorMin < 1 ? "weniger als 1 Minute" : `${vorMin} Min.`}
        </span>
      </span>
    </button>
  );
}

export function Mehrdeutig({ warenkorb, kandidaten, keinePasst }: { warenkorb: WK; kandidaten: { passend: Kandidat[]; andere: Kandidat[] } | null; keinePasst: () => void }) {
  const zuordnen = useZuordnen();
  const [wahl, setWahl] = useState<string | null>(kandidaten?.passend[0]?.transactionCode ?? null);
  const [fehler, setFehler] = useState<string | null>(null);
  const liste = kandidaten?.passend ?? [];
  return (
    <div className="sc-kasse">
      <section className="sc-kasse__cart kasse-flow">
        <StatusBadge status="ambiguous" size="lg" />
        <Notice tone="attention" title={`${liste.length} SumUp-Zahlungen passen`}>
          Alle über {formatEuro(warenkorb.summeCent)} in den letzten Minuten. Bitte die Zahlung wählen, die zu diesem Warenkorb gehört. Die andere bleibt für den nächsten Warenkorb frei.
        </Notice>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <div className="kasse-optionen" role="radiogroup" aria-label="Welche Zahlung gehört dazu?">
          {liste.map((k) => (
            <KandidatOption key={k.transactionCode} k={k} gewaehlt={wahl === k.transactionCode} onWahl={() => setWahl(k.transactionCode)} />
          ))}
        </div>
        <div className="sc-row">
          <Button
            variant="primary"
            size="lg"
            icon="check"
            disabled={!wahl}
            loading={zuordnen.isPending}
            onClick={async () => {
              if (!wahl) return;
              setFehler(null);
              try {
                await zuordnen.mutateAsync({ id: warenkorb.id, transactionCode: wahl });
              } catch (err) {
                setFehler(err instanceof ApiFehler ? err.message : "Zuordnung hat nicht geklappt.");
              }
            }}
          >
            Diese Zahlung zuordnen
          </Button>
          <Button variant="quiet" onClick={keinePasst}>
            Keine passt
          </Button>
        </div>
        <p className="sc-muted sc-small">
          <Icon name="circle-help" size={16} /> Tipp: Kundin oder Kunden fragen, ob mit Karte oder bar bezahlt wurde. Die Kartendetails stehen auf dem SumUp-Beleg.
        </p>
      </section>
      <aside className="sc-kasse__side">
        <Total amountCent={warenkorb.summeCent} label="Warenkorb" size="lg" caption={`${warenkorb.positionen.length} Artikel · geöffnet ${warenkorb.abgeschlossenAt ? formatUhrzeit(warenkorb.abgeschlossenAt) : ""}`} />
        <Positionen warenkorb={warenkorb} />
      </aside>
    </div>
  );
}

export function NichtGefunden({ warenkorb, kandidaten, zurueck }: { warenkorb: WK; kandidaten: { passend: Kandidat[]; andere: Kandidat[] } | null; zurueck?: () => void }) {
  const zuordnen = useZuordnen();
  const spaeter = useSpaeter();
  const abbrechen = useAbbrechen();
  const [wahl, setWahl] = useState<string | null>(null);
  const [notiz, setNotiz] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const andere = kandidaten?.andere ?? [];
  const seit = warenkorb.abgeschlossenAt ? formatUhrzeit(warenkorb.abgeschlossenAt) : "";
  const laeuft = zuordnen.isPending || spaeter.isPending || abbrechen.isPending;
  return (
    <div className="sc-kasse">
      <section className="sc-kasse__cart kasse-flow">
        <StatusBadge status="notfound" size="lg" />
        <h1 className="title">{warenkorb.status === "nicht_gefunden" ? "Keine passende Zahlung in 15 Minuten gefunden" : "Keine der Zahlungen passt"}</h1>
        <p className="sc-muted">SumUp hat seit {seit} keine Zahlung über {formatEuro(warenkorb.summeCent)} gemeldet, die zu diesem Warenkorb passt. Bitte wählen, wie es weitergeht.</p>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <div className="kasse-optionen">
          {warenkorb.status === "nicht_gefunden" ? (
            <button type="button" className="sc-option" onClick={() => spaeter.mutate({ id: warenkorb.id })} disabled={laeuft}>
              <Icon name="clock" size={24} />
              <span className="sc-option__text">
                <span className="sc-strong">Später zuordnen</span>
                <span className="sc-muted sc-small">Warenkorb bleibt offen und erscheint in der Verwaltung unter Verkäufe & Abgleich.</span>
              </span>
              <Icon name="chevron-right" size={20} />
            </button>
          ) : null}
          <div className="sc-option" style={{ flexDirection: "column", alignItems: "stretch", cursor: "default" }}>
            <span className="sc-row">
              <Icon name="list" size={24} />
              <span className="sc-option__text">
                <span className="sc-strong">Andere Zahlung wählen</span>
                <span className="sc-muted sc-small">Letzte SumUp-Zahlungen mit abweichendem Betrag</span>
              </span>
            </span>
            {andere.length === 0 ? (
              <p className="sc-muted sc-small" style={{ margin: "8px 0 0" }}>Keine weiteren Zahlungen in den letzten 30 Minuten.</p>
            ) : (
              <div className="sc-col" style={{ marginTop: 8 }}>
                {andere.map((k) => (
                  <KandidatOption key={k.transactionCode} k={k} gewaehlt={wahl === k.transactionCode} onWahl={() => setWahl(k.transactionCode)} mitAbweichung />
                ))}
              </div>
            )}
          </div>
          <button type="button" className="sc-option" onClick={() => confirm("Die Artikel gelten wieder als im Regal. Fortfahren?") && abbrechen.mutate({ id: warenkorb.id, grund: notiz || "Keine Zahlung gefunden" })} disabled={laeuft}>
            <Icon name="undo-2" size={24} />
            <span className="sc-option__text">
              <span className="sc-strong">Artikel wieder freigeben</span>
              <span className="sc-muted sc-small">Der Kauf hat nicht stattgefunden. Die Artikel gelten wieder als im Regal.</span>
            </span>
            <Icon name="chevron-right" size={20} />
          </button>
        </div>
        <div className="sc-row">
          {wahl ? (
            <Button
              variant="primary"
              size="lg"
              icon="check"
              loading={zuordnen.isPending}
              onClick={async () => {
                setFehler(null);
                try {
                  await zuordnen.mutateAsync({ id: warenkorb.id, transactionCode: wahl, notiz });
                } catch (err) {
                  setFehler(err instanceof ApiFehler ? err.message : "Zuordnung hat nicht geklappt.");
                }
              }}
            >
              Zahlung {formatEuro(andere.find((k) => k.transactionCode === wahl)?.betragCent ?? 0)} zuordnen
            </Button>
          ) : null}
          {zurueck ? (
            <Button variant="quiet" icon="chevron-left" onClick={zurueck}>
              Zurück zur Auswahl
            </Button>
          ) : null}
        </div>
      </section>
      <aside className="sc-kasse__side">
        <Total amountCent={warenkorb.summeCent} label="Offener Warenkorb" size="lg" caption={`${warenkorb.positionen.length} Artikel · seit ${seit}`} />
        <Positionen warenkorb={warenkorb} />
        <Textarea label="Notiz für den Abgleich" value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. Kundin hatte nur 20 € auf der Karte, Rest bar" rows={3} />
      </aside>
    </div>
  );
}
